import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

echarts.use([BarChart, LineChart, PieChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

/**
 * DECISION.md §2.6: Apache ECharts behind ONE internal `<Chart>` wrapper —
 * "every dashboard uses the wrapper; no screen touches ECharts options
 * directly" for RTL. The wrapper takes the app locale and emits the
 * RTL-corrected option: category axes inverted, legends right-aligned, and
 * number formatting left to the callers' preformatted strings (money never
 * becomes a JS number for display — §2.1).
 *
 * Callers memoize the option (ECharts treats a new object identity as a new
 * config); the wrapper resizes with its box.
 *
 * F-118 — the prototype's three hand-rolled scalings (the price trend's
 * 20+70% exaggeration, the cash net line's symmetric midline, the FX
 * history's 8% margins) existed because its bars had NO measuring axis.
 * Every chart here rides ECharts with real, labelled value axes, so the
 * scaling is readable off the axis and the rule's demand — "any chart whose
 * bars are not proportional must say so on screen" — is satisfied by
 * construction: there are no unlabelled non-proportional bars to warn about.
 */
export function Chart({ option, height = 240 }: { option: EChartsOption; height?: number }) {
  const { i18n } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const instance = useRef<echarts.ECharts | null>(null);
  const rtl = i18n.dir() === 'rtl';

  useEffect(() => {
    if (!box.current) return;
    instance.current = echarts.init(box.current);
    const observer = new ResizeObserver(() => instance.current?.resize());
    observer.observe(box.current);
    return () => {
      observer.disconnect();
      instance.current?.dispose();
      instance.current = null;
    };
  }, []);

  useEffect(() => {
    if (!instance.current) return;
    const corrected: EChartsOption = { ...option };
    if (rtl) {
      // The RTL preset: mirror category axes so bar order follows reading
      // order; keep VALUE axes (and the numbers on them) LTR.
      const flip = (axis: NonNullable<EChartsOption['xAxis']>) => {
        const list = Array.isArray(axis) ? axis : [axis];
        return list.map((a) => {
          const ax = a as { type?: string; inverse?: boolean };
          return ax.type === 'category' ? { ...ax, inverse: !ax.inverse } : ax;
        });
      };
      if (corrected.xAxis) corrected.xAxis = flip(corrected.xAxis) as NonNullable<EChartsOption['xAxis']>;
      if (corrected.legend && !Array.isArray(corrected.legend)) {
        corrected.legend = { ...corrected.legend, right: 0, left: 'auto' };
      }
    }
    instance.current.setOption(corrected, { notMerge: true });
  }, [option, rtl]);

  return <div ref={box} style={{ height }} aria-hidden="true" />;
}
